/**
 * Copyright Mántum All Rights Reserved.
 */
import Component from './component';
import Validator from '../validator';
import { EVENT_CLEAR, ACTION_VALUE, useGroupDispatch } from '../../group';
import { validator } from '@mantum/kanela';

import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import type { Detail, FormatTime as Format } from '@mantum/kanela';
import type { PropTypes } from '../internal/datetime/prop-types';

export default forwardRef<Detail<unknown>, PropTypes<Format>>(
    function Time(props, ref) {
        // prettier-ignore
        const {
        format = 'HH:mm',
        name,
        optional = true
    } = props;

        const inputRef = useRef<HTMLInputElement>(null);

        const dispatch = useGroupDispatch();

        const [error, setError] = useState(false);
        const [message, setMessage] = useState<string>();

        let enabled = props.validation?.date?.enabled;
        if (validator.isUndefined(enabled)) {
            enabled = true;
        }

        const validate = useCallback(
            (value?: string) => {
                setError(false);

                const empty = validator.isEmpty(value);
                if (!optional && empty) {
                    setError(true);
                    setMessage(props.validation?.message);
                    return false;
                }

                if (enabled && !empty) {
                    const result = validator.isDate(value, format);
                    if (!result) {
                        setError(true);
                        setMessage(props.validation?.date?.message);
                        return false;
                    }
                }

                return true;
            },
            [enabled, format, optional, props.validation]
        );

        useImperativeHandle(
            ref,
            () => {
                return {
                    name,
                    validable: true,
                    clear() {
                        setError(false);
                        if (name) {
                            dispatch({
                                type: EVENT_CLEAR,
                                content: [
                                    {
                                        action: ACTION_VALUE,
                                        target: name
                                    }
                                ]
                            });
                        }
                    },
                    validate() {
                        return validate(inputRef.current?.value);
                    },
                    value() {
                        return inputRef.current?.value;
                    }
                };
            },
            [name, validate, dispatch]
        );

        const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            validate(event.target.value);
            if (props.onBlur) {
                props.onBlur(event);
            }
        };

        return (
            <Validator error={error} message={message} type='time'>
                <Component
                    {...props}
                    error={error}
                    format={format}
                    onBlur={handleBlur}
                    onChange={props.onChange}
                    ref={inputRef}
                    value={props.value}
                />
            </Validator>
        );
    }
);
